/* Anime-Pass — cart drawer
   ---------------------------------------------------------
   Opens from any [data-ap-cart-open], updates line quantities through
   /cart/change.js, and re-renders itself from the Section Rendering API
   so prices, discounts and the shipping meter always come from Shopify
   rather than being recalculated here.

   Listens for `ap:cart:updated` (dispatched by the product page after an
   add) so the two never need to know about each other.

   Without JS every control is a real link or form posting to /cart, so
   the page navigates instead of updating in place. */
(function () {
  'use strict';

  var SECTION = 'anime-pass-cart-drawer';

  function root() {
    return document.querySelector('[data-ap-cart]');
  }

  function overlay() {
    return document.querySelector('[data-ap-cart-overlay]');
  }

  var lastFocused = null;
  var ownedInert = [];

  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function focusables(container) {
    return container.querySelectorAll(FOCUSABLE);
  }

  function setInert(state) {
    var d = root();
    if (!d) return;
    var section = d.closest('.shopify-section');
    var parent = section ? section.parentNode : d.parentNode;
    if (!parent) return;

    if (state) {
      ownedInert = [];
      Array.prototype.forEach.call(parent.children, function (sibling) {
        if (sibling === section || sibling === d) return;
        if (!sibling.hasAttribute('inert')) {
          sibling.setAttribute('inert', '');
          ownedInert.push(sibling);
        }
      });
    } else {
      ownedInert.forEach(function (el) {
        el.removeAttribute('inert');
      });
      ownedInert = [];
    }
  }

  function open() {
    var d = root();
    var o = overlay();
    if (!d || !o) return;
    var alreadyOpen = d.classList.contains('open');
    if (!alreadyOpen) {
      lastFocused = document.activeElement;
    }
    d.classList.add('open');
    o.classList.add('open');
    o.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (!alreadyOpen) {
      setInert(true);
    }
    if (!_recsLoaded) {
      _recsLoaded = true;
      loadUpsellRecommendations();
    }
    if (!alreadyOpen) {
      var close = d.querySelector('[data-ap-cart-close]');
      var target = close || focusables(d)[0];
      if (target) target.focus();
    }
    requestAnimationFrame(updateScrollHint);
  }

  function close() {
    var d = root();
    var o = overlay();
    if (!d || !o) return;
    d.classList.remove('open');
    o.classList.remove('open');
    o.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setInert(false);
    if (lastFocused) {
      try { lastFocused.focus(); } catch (_) {}
    }
    lastFocused = null;
  }

  function isOpen() {
    var d = root();
    return !!d && d.classList.contains('open');
  }

  function trap(e) {
    if (e.key !== 'Tab') return;
    var d = root();
    if (!d || !isOpen()) return;
    var items = focusables(d);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- Re-render ---------- */
  function cartStatus() {
    return document.querySelector('[data-ap-cart-status]');
  }

  function announceError() {
    var el = cartStatus();
    if (!el) return;
    var msg = el.getAttribute('data-t-error') || 'Unable to add item to cart. Please try again.';
    el.textContent = '';
    requestAnimationFrame(function () {
      el.textContent = msg;
    });
  }

  function announceCart(newCount, prevCount) {
    var el = cartStatus();
    if (!el) return;
    var msg;
    if (newCount === 0) {
      msg = 'Cart is empty';
    } else if (newCount > prevCount) {
      msg = 'Item added to cart. ' + newCount + (newCount === 1 ? ' item' : ' items') + ' in cart.';
    } else if (newCount < prevCount) {
      msg = 'Item removed from cart. ' + newCount + (newCount === 1 ? ' item' : ' items') + ' in cart.';
    } else {
      msg = 'Cart updated. ' + newCount + (newCount === 1 ? ' item' : ' items') + ' in cart.';
    }
    el.textContent = '';
    requestAnimationFrame(function () { el.textContent = msg; });
  }

  function swap(html) {
    var d = root();
    if (!d || !html) return;
    var prevCount = parseInt(d.getAttribute('data-ap-cart-count') || '0', 10);
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-ap-cart]');
    if (!fresh) return;
    d.innerHTML = fresh.innerHTML;
    var newCount = parseInt(fresh.getAttribute('data-ap-cart-count') || '0', 10);
    d.setAttribute('data-ap-cart-count', String(newCount));
    syncCount(parsed);
    _recsLoaded = true;
    loadUpsellRecommendations();
    bindScrollHint();
    document.dispatchEvent(new CustomEvent('ap:countdown:reinit'));
    announceCart(newCount, prevCount);
  }

  function syncCount(doc) {
    var source = (doc || document).querySelector('[data-ap-cart-count]');
    if (!source) return;
    var value = source.getAttribute('data-ap-cart-count');
    document.querySelectorAll('.cart-badge .count').forEach(function (el) {
      el.textContent = value === '0' ? '' : value;
    });
  }

  function refresh() {
    return fetch(window.location.pathname + '?section_id=' + SECTION)
      .then(function (r) {
        return r.text();
      })
      .then(swap)
      .catch(function () {});
  }

  /* ---------- Line item updates ---------- */
  function change(line, quantity, row) {
    if (row) row.classList.add('is-busy');
    return fetch((window.routes && window.routes.cart_change_url) || '/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: SECTION }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (state) {
        if (state.sections && state.sections[SECTION]) {
          swap(state.sections[SECTION]);
        } else {
          return refresh();
        }
      })
      .catch(function () {
        // Fall back to a full navigation rather than leaving a stuck row
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      });
  }

  /* ---------- Delegated events ---------- */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-ap-cart-open]');
    if (opener) {
      e.preventDefault();
      open();
      return;
    }

    if (e.target.closest('[data-ap-cart-close]') || e.target.closest('[data-ap-cart-overlay]')) {
      close();
      return;
    }

    var step = e.target.closest('[data-ap-line-step]');
    if (step) {
      e.preventDefault();
      var row = step.closest('[data-ap-line]');
      if (!row) return;
      var line = parseInt(row.getAttribute('data-ap-line'), 10);
      var current = parseInt(row.getAttribute('data-ap-line-qty'), 10) || 1;
      var next = current + parseInt(step.getAttribute('data-ap-line-step'), 10);
      change(line, Math.max(0, next), row);
      return;
    }

    var remove = e.target.closest('[data-ap-line-remove]');
    if (remove) {
      e.preventDefault();
      var r = remove.closest('[data-ap-line]');
      if (!r) return;
      change(parseInt(r.getAttribute('data-ap-line'), 10), 0, r);
      return;
    }

    var addBtn = e.target.closest('[data-ct-upsell-add]');
    if (addBtn) {
      e.preventDefault();
      if (addBtn.disabled) return;
      var vid = parseInt(addBtn.getAttribute('data-variant'), 10);
      addBtn.classList.add('is-adding');
      addBtn.innerHTML = '<span aria-hidden="true">…</span>';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }], sections: SECTION })
      })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (data) {
        addBtn.innerHTML = '<span aria-hidden="true">✓</span>';
        document.dispatchEvent(new CustomEvent('ap:cart:updated', {
          detail: { sections: data.sections || {} }
        }));
      })
      .catch(function () {
        addBtn.classList.remove('is-adding');
        addBtn.innerHTML = 'ADD <span aria-hidden="true">→</span>';
        announceError();
      });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen()) return;
    if (e.key === 'Escape') { close(); return; }
    trap(e);
  });

  // The product page fires this after a successful add
  document.addEventListener('ap:cart:updated', function (e) {
    var sections = e.detail && e.detail.sections;
    if (sections && sections[SECTION]) {
      swap(sections[SECTION]);
      open();
    } else {
      refresh().then(open);
    }
  });

  /* ---------- Upsell recommendations ---------- */
  var _esc = document.createElement('span');
  function esc(str) { _esc.textContent = str; return _esc.innerHTML; }

  function fmtMoney(cents, fmt) {
    var a = (cents / 100).toFixed(2);
    var aInt = Math.round(cents / 100).toString();
    var aComma = a.replace('.', ',');
    return fmt
      .replace('{{amount_with_comma_separator}}', aComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', aInt)
      .replace('{{amount_no_decimals}}', aInt)
      .replace('{{amount}}', a);
  }

  function loadUpsellRecommendations() {
    var el = document.querySelector('[data-ct-upsell-auto]');
    if (!el) return;

    var idsRaw = (el.getAttribute('data-product-ids') || '').split(',').filter(Boolean);
    var count = parseInt(el.getAttribute('data-count'), 10) || 4;
    var moneyFmt = el.getAttribute('data-money-format') || '{{amount}}';
    var tAdd = el.getAttribute('data-t-add') || 'ADD';
    var tAddAria = el.getAttribute('data-t-add-aria') || 'Add to cart';
    var track = el.querySelector('[data-ct-upsell-track]');
    if (!track) return;

    if (idsRaw.length === 0) { el.hidden = true; return; }

    var unique = [], cartSet = {};
    idsRaw.forEach(function (id) {
      id = id.trim();
      if (id && !cartSet[id]) { cartSet[id] = true; unique.push(id); }
    });

    var seedId = unique[unique.length - 1];
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root.replace(/\/$/, '') + '/recommendations/products.json?product_id=' + seedId + '&limit=' + (count + unique.length + 2) + '&intent=related')
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .catch(function () { return { products: [] }; })
      .then(function (result) {
      var used = {};
      for (var k in cartSet) used[k] = true;
      var picks = [];
      var products = result.products || [];

      for (var i = 0; i < products.length && picks.length < count; i++) {
        var p = products[i];
        if (p && !used[String(p.id)] && p.available !== false) {
          used[String(p.id)] = true;
          picks.push(p);
        }
      }

      if (picks.length === 0) { el.hidden = true; return; }

      el.hidden = false;
      track.innerHTML = picks.map(function (p) {
        var v = p.variants && p.variants[0];
        var vid = v ? v.id : '';
        var avail = v ? v.available : false;
        var price = p.price || (v ? v.price : 0);
        var img = p.featured_image
          ? p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&width=160' : '?width=160')
          : '';
        var title = esc(p.title || '');
        var url = p.url || ('/products/' + p.handle);
        return '<div class="ct-upsell-card">'
          + '<a href="' + url + '" class="ct-upsell-img">'
          + (img ? '<img src="' + img + '" alt="' + title + '" loading="lazy">' : '')
          + '</a>'
          + '<div class="ct-upsell-info">'
          + '<a href="' + url + '" class="ct-upsell-name">' + title + '</a>'
          + '<div class="ct-upsell-bottom">'
          + '<span class="ct-upsell-price">' + fmtMoney(price, moneyFmt) + '</span>'
          + '<button type="button" class="ct-upsell-add" data-ct-upsell-add data-variant="' + vid + '"' + (avail ? '' : ' disabled') + ' aria-label="' + esc(tAddAria) + '">' + esc(tAdd) + ' <span aria-hidden="true">→</span></button>'
          + '</div></div></div>';
      }).join('');
    });
  }

  /* ---------- Scroll peek indicator ---------- */
  // The scroll element depends on the mobile density toggle + viewport: when
  // mobile_upsell_scrolls is on at <=700px, .ct-scroll becomes the scroller
  // (it is display:contents otherwise); everywhere else .ct-items scrolls.
  // Pick whichever actually scrolls so both hooks follow the scroller.
  function scroller() {
    var s = document.querySelector('.ct-scroll');
    if (s && getComputedStyle(s).display !== 'contents') return s;
    return document.querySelector('.ct-items');
  }

  function updateScrollHint() {
    var wrap = scroller();
    if (!wrap) return;
    var hasMore = wrap.scrollHeight > wrap.clientHeight + 8 &&
                  wrap.scrollTop + wrap.clientHeight < wrap.scrollHeight - 8;
    wrap.classList.toggle('has-overflow', hasMore);
    // Clear a stale flag off the element that is not currently the scroller.
    var other = wrap.classList.contains('ct-scroll')
      ? document.querySelector('.ct-items')
      : document.querySelector('.ct-scroll');
    if (other && other !== wrap) other.classList.remove('has-overflow');
  }

  function bindScrollHint() {
    var wrap = scroller();
    if (!wrap) return;
    wrap.removeEventListener('scroll', updateScrollHint);
    wrap.addEventListener('scroll', updateScrollHint, { passive: true });
    updateScrollHint();
  }

  var _recsLoaded = false;

  function boot() {
    syncCount();
    bindScrollHint();
    _recsLoaded = false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  window.AnimePassCart = { open: open, close: close, refresh: refresh, sectionId: SECTION };

  /* ---- Quick Buy (cover cards) ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ap-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var variantId = btn.dataset.apQuickAdd;
    var orig = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span aria-hidden="true">…</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId), quantity: 1 }],
        sections: SECTION
      })
    })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(function (data) {
        btn.innerHTML = '<span aria-hidden="true">✓</span>';
        setTimeout(function () { btn.disabled = false; btn.innerHTML = orig; }, 1400);
        document.dispatchEvent(new CustomEvent('ap:cart:updated', {
          detail: { sections: data.sections || {} }
        }));
      })
      .catch(function () {
        btn.disabled = false;
        btn.innerHTML = orig;
        announceError();
      });
  });
})();
